/**
 * @fileoverview Visitor geolocation.
 * @module utils/geo
 *
 * @description
 * The SDK used to have NO geo at all. Location existed only in the no-code
 * tracking script, so a site tracked purely by this SDK reported no country
 * for anyone, and a visitor identified by the SDK — a signup, an auth
 * callback, a form capture — was unlocatable forever even though every other
 * detail about them was known. Anything that needs a country (the visitor map,
 * geo breakdowns, locale-aware segments) was silently empty.
 *
 * Resolution order, cheapest first:
 *   1. the `fg_geo` cookie — shared with the tracking script, so whichever
 *      ran first resolves it for both,
 *   2. geojs.io in the visitor's own browser,
 *   3. the FlowGrid backend, which resolves coarse geo from the request IP
 *      (the fallback that survives ad-blockers eating geojs).
 *
 * The result is mirrored back to `fg_geo` so it is readable by the tracking
 * script AND by the customer's server (see `geoFromCookies` in the server
 * entry) — a server request's own IP is the datacenter's, so this cookie is
 * the only location a server-side identify can trust.
 *
 * Never throws: every failure resolves to `null` and the caller carries on.
 */

import { getCookie } from './storage';

/** A visitor's resolved location. */
export interface VisitorGeo {
  /** ISO-3166 alpha-2 code, e.g. "ZA". */
  country: string;
  /** Full country name, e.g. "South Africa". */
  name: string;
  city: string;
  region: string;
}

export const GEO_COOKIE = 'fg_geo';
const GEO_COOKIE_DAYS = 365;

/** In-flight/settled resolution, so N callers cost ONE lookup per page load. */
let pending: Promise<VisitorGeo | null> | null = null;

function isBrowser(): boolean {
  return typeof document !== 'undefined' && typeof window !== 'undefined';
}

/** Cookie domain that also covers subdomains, matching the tracking script. */
function cookieDomain(): string {
  const parts = window.location.hostname.split('.');
  return parts.length >= 2 ? `.${parts.slice(-2).join('.')}` : window.location.hostname;
}

/** The location already resolved for this visitor, or null. Synchronous. */
export function readGeoCookie(): VisitorGeo | null {
  const raw = getCookie(GEO_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as Partial<VisitorGeo>;
    const geo: VisitorGeo = {
      country: String(parsed.country ?? ''),
      name: String(parsed.name ?? ''),
      city: String(parsed.city ?? ''),
      region: String(parsed.region ?? ''),
    };
    return geo.country || geo.name ? geo : null;
  } catch {
    return null;
  }
}

/** Mirror a resolved location to the shared cookie. Best-effort. */
export function writeGeoCookie(geo: VisitorGeo): void {
  if (!isBrowser()) return;
  if (!geo.country && !geo.name) return; // never persist an empty location
  try {
    const expires = new Date(Date.now() + GEO_COOKIE_DAYS * 864e5).toUTCString();
    const value = encodeURIComponent(JSON.stringify(geo));
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      `${GEO_COOKIE}=${value}; expires=${expires}; path=/; domain=${cookieDomain()}; SameSite=Lax${secure}`;
  } catch {
    /* cookie write is best-effort */
  }
}

function normalize(raw: Record<string, unknown> | null | undefined): VisitorGeo | null {
  if (!raw) return null;
  const geo: VisitorGeo = {
    // geojs returns `country_code`/`country`; the FlowGrid fallback returns
    // `country`/`name` already normalized. Accept both shapes.
    country: String(raw.country_code ?? raw.country ?? ''),
    name: String(raw.name ?? raw.country ?? ''),
    city: String(raw.city ?? ''),
    region: String(raw.region ?? ''),
  };
  // `country` must be the ISO code. When only a full name came back, don't
  // pass it off as a code — downstream builds flag URLs from a 2-letter code.
  if (geo.country.length !== 2) geo.country = '';
  return geo.country || geo.name ? geo : null;
}

async function fetchJson(url: string, ms: number): Promise<Record<string, unknown> | null> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ms);
    try {
      const res = await fetch(url, { signal: ctrl.signal });
      if (!res.ok) return null;
      return (await res.json()) as Record<string, unknown>;
    } finally {
      clearTimeout(timer);
    }
  } catch {
    return null; // blocked, offline, timed out — all the same to us
  }
}

/**
 * Resolve the visitor's location, memoized for the page load. Returns null
 * when every source failed (offline, geojs blocked AND backend unreachable).
 *
 * @param endpoint - normalized API base, e.g. `https://core.flow-grid.xyz`
 */
export async function resolveGeo(endpoint: string): Promise<VisitorGeo | null> {
  if (!isBrowser()) return null;

  const cached = readGeoCookie();
  if (cached) return cached;
  if (pending) return pending;

  pending = (async () => {
    const fromGeojs = normalize(await fetchJson('https://get.geojs.io/v1/ip/geo.json', 4000));
    if (fromGeojs) {
      writeGeoCookie(fromGeojs);
      return fromGeojs;
    }
    // geojs blocked or down — the backend resolves from the request IP.
    const res = await fetchJson(`${endpoint}/api/V1/visits/countries`, 4000);
    const fromServer = normalize((res?.geo as Record<string, unknown>) ?? null);
    if (fromServer) writeGeoCookie(fromServer);
    return fromServer;
  })();

  try {
    return await pending;
  } finally {
    // Keep a successful result memoized via the cookie; allow a retry on the
    // next call if everything failed (a transient outage shouldn't be sticky).
    if (!readGeoCookie()) pending = null;
  }
}

/**
 * Resolve the visitor's location AND report it, so the site gets geo rows even
 * when the no-code tracking script isn't installed. Fire-and-forget.
 */
export async function ensureLocation(
  endpoint: string,
  webId: string,
  visitorId: string,
): Promise<VisitorGeo | null> {
  const geo = await resolveGeo(endpoint);
  if (!geo || !webId || !visitorId) return geo;

  const body = JSON.stringify({
    web_id: webId,
    visitor_id: visitorId,
    path_name: isBrowser() ? window.location.pathname : '/',
    name: geo.name,
    country: geo.country,
    country_code: geo.country,
    city: geo.city,
    region: geo.region,
  });

  try {
    await fetch(`${endpoint}/api/V1/visits/countries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch {
    /* never throws — an unreported location must not break tracking */
  }
  return geo;
}

/** Test seam: forget the memoized in-flight resolution. */
export function __resetGeoCache(): void {
  pending = null;
}
